function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { Scene, PointLight, PerspectiveCamera, WebGLRenderer, AnimationMixer, Clock, AmbientLight, sRGBEncoding, HemisphereLight, LoopOnce } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { getData } from 'src/utils/cacheStorage';

/**
 * Camera Configs
 */
const FOV = 35;
const CAMERA_NEAR = 1;
const CAMERA_FAR = 2000;
export class Hero3DRenderer {
  //Observer
  // Clock
  // Controls
  // Scene
  // Camera
  //Renderer
  // Light
  // Loader
  // Animations
  constructor(idleSrc) {
    _defineProperty(this, "resizeObserver", undefined);

    _defineProperty(this, "resizeCallback", undefined);

    _defineProperty(this, "clock", new Clock());

    _defineProperty(this, "controller", undefined);

    _defineProperty(this, "scene", new Scene());

    _defineProperty(this, "camera", new PerspectiveCamera(FOV, 1, CAMERA_NEAR, CAMERA_FAR));

    _defineProperty(this, "renderer", new WebGLRenderer({
      alpha: true,
      antialias: true
    }));

    _defineProperty(this, "rootNode", undefined);

    _defineProperty(this, "ambientLight", new AmbientLight(0xffffff, 0.5));

    _defineProperty(this, "pointLight", new PointLight(0xffffff, 1, 300));

    _defineProperty(this, "gltfLoader", new GLTFLoader());

    _defineProperty(this, "animationMixer", undefined);

    _defineProperty(this, "animationActions", {
      idle: {
        weight: 0
      },
      highlight: {
        weight: 1
      }
    });

    _defineProperty(this, "idleModel", undefined);

    _defineProperty(this, "isReady", undefined);

    _defineProperty(this, "centerModel", scaleRatio => {
      if (!this.idleModel) return;
      const ratio = 70 * scaleRatio;
      this.idleModel.scene.scale.set(ratio, ratio, ratio);
      this.idleModel.scene.position.y = -120;
    });

    _defineProperty(this, "activeAction", undefined);

    this.loadModel = this.loadModel.bind(this);
    this.render = this.render.bind(this);
    this.mountRenderer = this.mountRenderer.bind(this);
    this.animate = this.animate.bind(this);
    this.restoreState = this.restoreState.bind(this);
    this.destroy = this.destroy.bind(this);
    const loadModel = this.loadModel;

    async function loader() {
      await loadModel(idleSrc);
      return true;
    }

    this.isReady = loader();
  }

  destroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  async loadModel(idleSrc) {
    const data = await getData(idleSrc);

    if (data) {
      this.idleModel = await this.gltfLoader.parseAsync(data, '');
    } else {
      this.idleModel = await this.gltfLoader.loadAsync(idleSrc);
    }

    this.animationMixer = new AnimationMixer(this.idleModel.scene);

    if (this.idleModel.animations.length) {
      const idleAction = this.idleModel.animations.find(e => e.name.toLowerCase() === 'idle') || this.idleModel.animations[0];
      this.animationActions.idle.action = this.animationMixer.clipAction(idleAction);
      const highlightAction = this.idleModel.animations.find(e => e.name.toLowerCase() === 'highlight') || this.idleModel.animations[0];
      this.animationActions.highlight.action = this.animationMixer.clipAction(highlightAction);
      this.animationActions.highlight.action.clampWhenFinished = true;
      this.animationActions.highlight.action.loop = LoopOnce;
    }
  }

  playAnimation() {
    const idleAction = this.animationActions.idle.action;
    const highlightAction = this.animationActions.highlight.action;

    if (highlightAction && idleAction && this.animationMixer && this.activeAction !== highlightAction) {
      this.fadeToAction(highlightAction, 0.2);
      this.animationMixer.addEventListener('finished', this.restoreState);
    }
  }

  restoreState() {
    if (this.animationMixer) this.animationMixer.removeEventListener('finished', this.restoreState);

    if (this.animationActions.idle.action) {
      this.fadeToAction(this.animationActions.idle.action, 0.2);
    }
  }

  fadeToAction(action, duration) {
    const previousAction = this.activeAction;
    this.activeAction = action;

    if (previousAction && previousAction !== this.activeAction) {
      previousAction.fadeOut(duration);
    }

    this.activeAction.reset().setEffectiveTimeScale(1).setEffectiveWeight(1).fadeIn(duration).play();
  }

  mountRenderer(mountNode) {
    this.rootNode = mountNode;
    this.rootNode.appendChild(this.renderer.domElement);

    this.resizeCallback = entries => {
      if (entries.length) {
        const {
          width,
          height
        } = entries[0].contentRect;
        this.camera.aspect = width / height;
        this.renderer.setSize(width, height);
      }
    };

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(this.resizeCallback);
    }

    this.resizeObserver.observe(this.rootNode);
  } // eslint-disable-next-line max-statements


  async render(scaleRatio) {
    await this.isReady;
    if (!this.rootNode || !this.idleModel) return;
    const {
      width,
      height
    } = this.rootNode.getBoundingClientRect();
    this.scene.add(this.ambientLight);
    this.pointLight.position.set(0, 0, 200);
    const hemiLight = new HemisphereLight(0xffffff, 0x444444);
    hemiLight.position.set(0, 30, 0);
    this.scene.add(hemiLight);
    this.camera.aspect = width / height;
    this.camera.position.set(100, 50, 850);
    this.camera.updateProjectionMatrix();
    this.controller = new OrbitControls(this.camera, this.renderer.domElement);
    this.controller.enableDamping = false;
    this.controller.enableZoom = false;
    this.controller.enablePan = false;
    this.controller.maxPolarAngle = Math.PI / 2;
    this.controller.minPolarAngle = Math.PI / 2;
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.outputEncoding = sRGBEncoding;
    this.scene.add(this.idleModel.scene);
    this.playAnimation(); // detect event Click into model (not Rotate model) then display Highlight action

    let angle = this.controller.getAzimuthalAngle();
    this.controller.addEventListener('end', () => {
      var _this$controller;

      const currentAngle = (_this$controller = this.controller) === null || _this$controller === void 0 ? void 0 : _this$controller.getAzimuthalAngle();

      if (angle === currentAngle) {
        this.playAnimation();
      } else angle = currentAngle || angle;
    });
    this.centerModel(scaleRatio);
    this.animate();
  }

  animate() {
    if (!this.controller) return;
    requestAnimationFrame(this.animate);
    this.controller.update();

    if (this.animationMixer) {
      this.animationMixer.update(this.clock.getDelta());
    }

    this.renderer.render(this.scene, this.camera);
  }

}